// AC GUIDE 9: HOT AND COLD AC, IS IT WORTH IT IN DELHI? (1 Nov 2026)
// /blog/hot-and-cold-ac
//
// Written to GUIDES_PLAYBOOK.md section 3, in the new layout (guide 7
// onwards): the answer, the animation, the simple table, one rupee example,
// pros and cons, short parts, where to see it, questions. No tool, and no
// "From our showroom" tip (Saheb had none, 31 Oct 2026).
//
// THE ANSWER IS SAHEB'S (31 Oct 2026): a hot and cold AC for a bedroom used
// in winter, for a home with elderly parents or small children, or a home
// with no other heating. He sees no real downside, so the cons list holds
// only plain Daikin facts. He believes it is healthier than room heaters,
// "better air to breathe"; the page keeps only what an allowed source backs:
// it burns nothing, so it gives off no fumes (Daikin: it moves heat instead
// of making it; NDMA: kerosene, heaters and coal ovens need ventilation to
// avoid toxic fumes), and the FTHT carries a PM1.0 filter (Daikin). Left out
// for lack of an allowed source: oxygen, dry air, skin, and any percentage
// saving over a room heater (Daikin's blog cites only "some sources").
//
// Prices, stock and models are never typed here: callers pass heatPair()
// for each size, read from catalogue.js and pricing.js. No imports.
// **double stars** mean bold.

export const HEAT_SLUG = 'blog/hot-and-cold-ac';
export const HEAT_PUBLISHED = '2026-11-01';
export const HEAT_UPDATED = '2026-11-01';

export const HEAT_SEO = {
  title: 'Hot and Cold AC: Is It Worth It in Delhi? | Citi Cool',
  meta: 'Is a hot and cold AC worth it in Delhi? Yes, for a bedroom you use in winter, or a home with elderly parents or small children. One AC all year, no fumes.',
  eyebrow: 'AC Guide · Buying',
  h1: 'Hot and cold AC: is it worth it in Delhi?',
  crumb: 'Hot and cold AC',
  readMins: 4,
};

export const RATE = 8;                 // ₹ a unit, as in every guide
export const SIZES = ['1.0', '1.5', '1.8'];
// Daikin FTHT50XV page: operating current in heating, 6.54 A. Full power =
// 230 V × rated current, as in the AC calculator (playbook rule 2.8).
export const HEAT_AMPS = { '1.5': 6.54 };
export const HEAT_MIN_C = -10;         // Daikin: heating works down to -10°C outside
// IMD: Delhi's lowest ever, -0.6°C at Safdarjung on 16 Jan 1935; January 2026's
// lowest, 2.9°C at Safdarjung on 15 Jan.
export const DELHI_RECORD_C = -0.6;
export const DELHI_JAN26_C = 2.9;

// ─── sources ──────────────────────────────────────────────────────────────

export const HEAT_SOURCES = [
  { key: 'saheb', label: 'Our showroom advice: a hot and cold AC for a bedroom used in winter, a home with elderly parents or small children, or a home with no other heating (October 2026)', by: 'Citi Cool', href: null },
  { key: 'daikin-ftht', label: 'FTHT50XV Hot & Cold split AC: heating from -10°C to 24°C outside, heating current, PM1.0 filter, inverter', by: 'Daikin India', href: 'https://daikinindia.com/product-category/splitac/hot-cold-series/FTHT50XV' },
  { key: 'daikin-hc', label: "Daikin's Hot and Cold Split AC: Your Partner in All Seasons (a heat pump in winter; no separate room heater or blower needed)", by: 'Daikin India', href: 'https://daikinindia.com/blog-details/daikin%E2%80%99s-hot-and-cold-split-ac-your-partner-all-seasons' },
  { key: 'daikin-year', label: "Here's how we ensure comfort all year round (a heat pump moves heat instead of making it, and uses much less electricity than a room heater)", by: 'Daikin India', href: 'https://daikinindia.com/blog-details/heres-how-we-ensure-comfort-all-year-round' },
  { key: 'ndma', label: 'Cold Wave: do\'s and don\'ts ("Maintain proper ventilation when using a kerosene, heater or coal oven to avoid toxic fumes")', by: 'National Disaster Management Authority', href: 'https://ndma.gov.in/Resources/awareness/Cold_Wave' },
  { key: 'imd-record', label: "Lowest minimum temperature over Delhi on 1 Jan 2021: Delhi's all-time lowest, -0.6°C on 16 Jan 1935", by: 'India Meteorological Department', href: 'https://internal.imd.gov.in/press_release/20210101_pr_972.pdf' },
  { key: 'imd-jan26', label: 'Monthly weather report of Delhi, January 2026: lowest 2.9°C at Safdarjung', by: 'India Meteorological Department', href: 'https://mausam.imd.gov.in/newdelhi/archive/2026-02-03/press_release_en.pdf' },
  { key: 'daikin-label', label: 'BEE star label figures from the Daikin RA Master Catalogue 2026 (ISEER and units a year), shown on each product page', by: 'Daikin', href: '/daikin-hot-and-cold-ac-price' },
  { key: 'derate', label: "Daikin's full-cooling outside temperature for each series, as used in our AC calculator", by: 'Daikin · Citi Cool engineering', href: '/ac-calculator' },
  { key: 'calc', label: "Full power = 230 V × Daikin's rated current, as in our AC calculator", by: 'Citi Cool engineering', href: '/ac-calculator' },
  { key: 'daikin-warranty', label: 'Product Warranty', by: 'Daikin India', href: 'https://www.daikinindia.com/node/34556' },
  { key: 'prices', label: 'Daikin hot and cold AC prices and stock today', by: 'citicool.in', href: '/daikin-hot-and-cold-ac-price' },
];

// ─── the ACs ──────────────────────────────────────────────────────────────

export const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
export const inr100 = (n) => `₹${(Math.round(n / 100) * 100).toLocaleString('en-IN')}`;
const codeOf = (idu) => (String(idu).match(/^[A-Z]+\d+/) || [idu])[0];

// The FTHT of one size, and the cooling-only 3 Star inverter of the same size
// to compare it with, both WITH the piping kit (FTHT is with-kit only), so the
// gap is the heating and nothing else. Cooling-only pick: in stock first, then
// not sold out, then the cheapest. Returns null when either is missing.
export function heatPair({ catalogue, prices, stock, series, size }) {
  const KIT = 'withKit';
  const entry = (m) => {
    const left = (stock[m.id] || {})[KIT];
    return {
      model: m, kit: KIT, price: (prices[m.id] || {})[KIT], code: codeOf(m.variants[KIT].idu),
      inStock: (left || 0) > 0, soldOut: left === 0,
      full: (series[m.series] && series[m.series].heat && series[m.series].heat.full) || 43,
    };
  };
  const priced = (m) => m.soldAs === size && m.variants[KIT] && (prices[m.id] || {})[KIT] > 0;
  const hc = catalogue.find((m) => priced(m) && m.series === 'FTHT');
  const cool = catalogue
    .filter((m) => priced(m) && m.inverter && m.series !== 'FTHT' && m.stars === (hc ? hc.stars : 3))
    .map(entry)
    .sort((a, b) => (Number(b.inStock) - Number(a.inStock)) || (Number(a.soldOut) - Number(b.soldOut)) || (a.price - b.price))[0];
  if (!hc || !cool) return null;
  return { size, hc: entry(hc), cool };
}

// Units an hour at full power while heating, where Daikin publishes the current.
export const heatUnits = (size) => (HEAT_AMPS[size] ? (HEAT_AMPS[size] * 230) / 1000 : null);

// ─── the page ─────────────────────────────────────────────────────────────

// pairs: heatPair() for SIZES (nulls dropped). The 1.5 ton pair leads.
export function heatContent(pairs) {
  const p = pairs.find((x) => x.size === '1.5') || pairs[0];
  const H = p.hc; const C = p.cool;
  const gap = H.price - C.price;
  const u = heatUnits(p.size);
  const sameLabel = H.model.annualKwh === C.model.annualKwh;
  const sizesText = pairs.map((x) => `${Number(x.size)}`).join(', ').replace(/, ([^,]*)$/, ' and $1');

  return {
    answer: {
      text: '**Buy a hot and cold AC for a bedroom you use in winter.** Buy one too if elderly parents or small children live with you. Need the AC only in summer? Buy a cooling-only one.',
      src: ['saheb'],
    },
    because: [
      { text: `It costs about ${inr100(gap)} more than a cooling-only 3 Star. It also replaces a room heater.`, src: ['prices', 'daikin-hc'] },
      { text: 'It burns nothing, so it gives off no fumes.', src: ['daikin-year', 'ndma'] },
      { text: `It keeps heating down to ${HEAT_MIN_C}°C outside. Delhi's coldest day ever was ${DELHI_RECORD_C}°C.`, src: ['daikin-ftht', 'imd-record'] },
    ],
    watch: {
      title: 'Watch: how one AC cools in summer and heats in winter',
      captions: [
        { at: 0, text: 'In summer, the AC takes heat out of your room. It throws it outside.' },
        { at: 0.3, text: 'In winter, a hot and cold AC runs the other way.' },
        { at: 0.5, text: 'It pulls heat from the cold air outside. It brings it into your room.' },
        { at: 0.78, text: 'It moves heat. It burns nothing, so there are no fumes.' },
      ],
      note: `A simple drawing. Daikin's hot and cold ACs keep heating down to ${HEAT_MIN_C}°C outside.`,
      noteSrc: ['daikin-year', 'daikin-ftht'],
    },
    table: {
      title: 'Hot and cold AC or cooling only: which should you buy?',
      head: ['Your home', 'Choose'],
      rows: [
        ['A bedroom you use in winter too', 'Hot and cold'],
        ['Elderly parents or small children at home', 'Hot and cold'],
        ['No other heating at home', 'Hot and cold'],
        ['A room you use only in summer', 'Cooling only'],
        ['Long summer days, lowest summer bills', 'Cooling only 5 Star'],
      ],
      note: 'The rule: **need heat in winter? Hot and cold. Summer only? Cooling only.** For long summer hours, see [3 Star vs 5 Star](/blog/3-star-vs-5-star-ac).',
      src: ['saheb'],
    },
    example: {
      title: 'In rupees: one real example',
      lines: [
        { label: `${Number(p.size)} ton hot and cold, ${H.code}, with piping kit`, value: inr(H.price) },
        { label: `${Number(p.size)} ton cooling only 3 Star, ${C.code}, with piping kit`, value: inr(C.price) },
        ...(u ? [{ label: 'Heating at full power, for one hour', value: `about ${Math.round(u * 10) / 10} units` }] : []),
      ],
      text: `The hot and cold AC costs about ${inr100(gap)} more today. Both come with the piping kit.${u ? ` At full power it heats for about ${inr(u * RATE)} an hour, at ${inr(RATE)} a unit.` : ''} Once the room is warm, it slows down and uses less.`,
      src: ['prices', 'daikin-ftht', 'calc'],
    },
    sides: [
      {
        key: 'hc',
        title: 'Hot and cold AC: pros and cons',
        good: [
          { text: 'One AC cools in summer and heats in winter.', src: ['daikin-ftht'] },
          { text: 'You don\'t need a separate room heater or blower.', src: ['daikin-hc'] },
          { text: 'It burns nothing. So there are no fumes in a closed room.', src: ['daikin-year', 'ndma'] },
          { text: 'Its PM1.0 filter catches very fine dust.', src: ['daikin-ftht'] },
          { text: 'It stops heating once the room is warm enough.', src: ['daikin-year'] },
        ],
        bad: [
          { text: `It costs about ${inr100(gap)} more than a cooling-only 3 Star.`, src: ['prices'] },
          { text: 'It comes only as a 3 Star. A 5 Star uses fewer units in summer.', src: ['daikin-label'] },
          { text: `It comes only in ${sizesText} ton.`, src: ['prices'] },
        ],
      },
      {
        key: 'cool',
        title: 'Cooling-only AC: pros and cons',
        good: [
          { text: 'It costs less to buy.', src: ['prices'] },
          { text: 'You can pick a 5 Star for long summer hours.', src: ['daikin-label'] },
          { text: 'There are more models and sizes to choose from.', src: ['prices'] },
        ],
        bad: [
          { text: 'It can\'t heat. In winter you need a room heater too.', src: ['daikin-hc'] },
          { text: 'Kerosene and coal heaters need fresh air to avoid fumes.', src: ['ndma'] },
          { text: 'A room heater uses a lot of electricity the whole time it is on.', src: ['daikin-year'] },
        ],
      },
    ],
    parts: [
      {
        id: 'how',
        q: 'How does a hot and cold AC heat a room?',
        bullets: [
          { text: 'In summer, an AC takes heat from your room and moves it outside.', src: ['daikin-year'] },
          { text: 'In winter, a hot and cold AC does the opposite. It brings heat in from outside.', src: ['daikin-year'] },
          { text: 'This is called a heat pump. It moves heat. It does not make heat by burning.', src: ['daikin-hc', 'daikin-year'] },
        ],
      },
      {
        id: 'heater',
        q: 'Is a hot and cold AC better than a room heater?',
        bullets: [
          { text: 'It gives off no fumes. The government says kerosene and coal heaters need fresh air.', src: ['daikin-year', 'ndma'] },
          { text: 'It uses much less electricity than a room heater, says Daikin.', src: ['daikin-year'] },
          { text: 'It stops heating once the room is warm. A heater often keeps going.', src: ['daikin-year'] },
          { text: 'It also cleans the air. Its PM1.0 filter catches very fine dust.', src: ['daikin-ftht'] },
        ],
      },
      {
        id: 'bill',
        q: 'How much electricity does a hot and cold AC use?',
        bullets: [
          ...(u ? [{ text: `A 1.5 ton uses at most about ${Math.round(u * 10) / 10} units an hour while heating.`, src: ['daikin-ftht', 'calc'] }] : []),
          { text: 'Once the room is warm, it slows down and uses less. See [how an inverter works](/blog/difference-between-inverter-and-non-inverter-ac).', src: ['daikin-ftht'] },
          ...(sameLabel ? [{ text: `In summer, it uses the same units as a cooling-only 3 Star. See [units per hour](/blog/ac-power-consumption-units-per-hour).`, src: ['daikin-label'] }] : []),
        ],
      },
      {
        id: 'delhi',
        q: 'Does a hot and cold AC work in Delhi winters?',
        bullets: [
          { text: `Yes. It keeps heating down to ${HEAT_MIN_C}°C outside.`, src: ['daikin-ftht'] },
          { text: `Delhi's coldest day in January 2026 was ${DELHI_JAN26_C}°C.`, src: ['imd-jan26'] },
          { text: `Its coldest day ever was ${DELHI_RECORD_C}°C, in 1935.`, src: ['imd-record'] },
        ],
      },
      {
        id: 'size',
        q: 'What size hot and cold AC do I need?',
        bullets: [
          { text: `We sell them in ${sizesText} ton.`, src: ['prices'] },
          { text: 'Pick the size for summer cooling. It heats about as much as it cools.', src: ['daikin-ftht'] },
          { text: 'Our guide shows [what size AC you need](/blog/what-size-ac-do-i-need) for your room.', src: [] },
        ],
      },
    ],
    fits: [
      { label: 'Prices today', title: 'Daikin hot and cold ACs', text: `${sizesText} ton, with stock and price.`, href: '/daikin-hot-and-cold-ac-price' },
      { label: 'Most bought size', title: `${Number(p.size)} ton hot and cold, ${H.code}`, text: H.inStock ? 'In stock, 24-hour delivery.' : 'Available on order.', href: `/${H.model.slug}` },
    ],
    faq: [
      {
        q: 'Is a hot and cold AC worth it in Delhi?',
        a: `Yes, if you use the room in winter. It costs about ${inr100(gap)} more than a cooling-only 3 Star. And you skip a room heater.`,
        src: ['saheb', 'prices', 'daikin-hc'],
      },
      {
        q: 'Can a hot and cold AC replace a room heater?',
        a: 'Yes. Daikin says you don\'t need a separate room heater or blower.',
        src: ['daikin-hc'],
      },
      {
        q: 'Is a hot and cold AC good for elderly people and small children?',
        a: 'Yes, it is what we suggest for them. It burns nothing, so there are no fumes in a closed room.',
        src: ['saheb', 'daikin-year', 'ndma'],
      },
      {
        q: 'Does a hot and cold AC use more electricity in summer?',
        a: sameLabel ? 'No. Its star label shows the same units as a cooling-only 3 Star.' : 'About the same as a cooling-only 3 Star. Check each star label.',
        src: ['daikin-label'],
      },
      {
        q: 'Down to what temperature does a hot and cold AC heat?',
        a: `Daikin's hot and cold ACs heat when it is as cold as ${HEAT_MIN_C}°C outside.`,
        src: ['daikin-ftht'],
      },
      {
        q: 'What is the warranty on a hot and cold AC?',
        a: 'The same as any Daikin inverter AC. 1 year on the AC, 5 on the circuit board, and 10 on the compressor.',
        src: ['daikin-warranty'],
      },
    ],
  };
}
